import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { BillingItemType, BillingItem } from '../model/BillingItem';
import { Money } from './currency';

export const getBillingItemTitle = (item: BillingItem, t: TFunction) => {
  switch (item.type) {
    case BillingItemType.SUBSCRIPTION:
      return t(`subscriptions.${item.subscriptionId}_LONG`);
    case BillingItemType.RECURRING:
      return t(`recurring.${item.recurringType}`);
    case BillingItemType.ONE_TIME:
      return item.name;
    default:
      return 'unknown';
  }
};

export const getStartDate = (billingItem: BillingItem) => {
  if (billingItem.type === BillingItemType.ONE_TIME) {
    return billingItem.datePaid;
  }
  if (billingItem.type === BillingItemType.USAGE) {
    return billingItem.period;
  }
  return billingItem.startDate
    ? DateTime.fromISO(billingItem.startDate).toFormat('yyyy-MM-dd')
    : '';
};

export const getEndDate = (billingItem: BillingItem) => {
  if (billingItem.type === BillingItemType.ONE_TIME) {
    return '';
  }
  if (billingItem.type === BillingItemType.USAGE) {
    return '';
  }
  // if billingItem endDate is more than 10 years in the future, return 'löpande'
  if (billingItem.endDate && DateTime.fromISO(billingItem.endDate).diffNow().as('years') > 10) {
    return 'löpande';
  }
  return billingItem.endDate ? DateTime.fromISO(billingItem.endDate).toFormat('yyyy-MM-dd') : '';
};

/**
 * Calculate the number of active days for a billing item in a given month.
 * @param month - The month to calculate the active days for. Format: yyyy-MM
 * @param startDate - The start date of the billing item. ISO format.
 * @param endDate - The end date of the billing item. ISO format.
 * @returns The number of active days for the billing item in the given month.
 */
const calculateActiveDays = (
  month: string,
  startDate: string,
  endDate?: string | null,
  paidUntil?: string | null,
) => {
  const startOfMonth = DateTime.fromFormat(month, 'yyyy-MM').plus({ months: 1 }).startOf('month');
  const endOfMonth = DateTime.fromFormat(month, 'yyyy-MM').plus({ months: 1 }).endOf('month');

  const startDateDateTime = DateTime.fromISO(startDate);
  const endDateDateTime = endDate ? DateTime.fromISO(endDate) : endOfMonth;

  let effectiveStart = startDateDateTime;
  if (startDateDateTime < startOfMonth) {
    effectiveStart = startOfMonth;
  }
  if (paidUntil) {
    const paidUntilDateTime = DateTime.fromISO(paidUntil);

    effectiveStart = paidUntilDateTime.plus({ days: 1 }); // the day after the paidUntil date
  }

  let effectiveEnd = endDateDateTime;
  if (endDateDateTime > endOfMonth) {
    effectiveEnd = endOfMonth;
  }

  return calculateMonthPercentage(effectiveStart, effectiveEnd);
};

/**
 * Calculate the percentage of months between two dates.
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns The percentage of months (can be > 1 for multiple months)
 */
export const calculateMonthPercentage = (startDate: DateTime, endDate: DateTime): number => {
  if (startDate > endDate) {
    return 0;
  }

  const startOfStartDateMonth = startDate.startOf('month');
  const endOfStartDateMonth = startDate.endOf('month');
  const startOfEndDateMonth = endDate.startOf('month');
  const endOfEndDateMonth = endDate.endOf('month');

  let { months } = startOfStartDateMonth.diff(endOfEndDateMonth, 'months');
  months = Math.abs(Math.round(months));
  // decrease with the part of a month that is between start of startt month and startDate
  const startOfStartDate = startDate.startOf('day');
  const daysInStartDateMonth = startOfStartDate.diff(startOfStartDateMonth, 'days').days;
  const daysInStartDate = Math.round(endOfStartDateMonth.diff(startOfStartDateMonth, 'days').days);

  const percentage = daysInStartDateMonth / daysInStartDate;
  months -= percentage;

  // decrease with the part of a month that is between end of endDate month and endDate
  const endOfEndDate = endDate.endOf('day');
  const dayAfterEndDate = Math.round(endOfEndDateMonth.diff(endOfEndDate, 'days').days);
  const daysInEndDateMonth = Math.round(endOfEndDateMonth.diff(startOfEndDateMonth, 'days').days);
  months -= dayAfterEndDate / daysInEndDateMonth;

  return months;
};

export const getBillingItemQuantity = (billingItem: BillingItem, month: string) => {
  if (billingItem.type === BillingItemType.ONE_TIME || billingItem.type === BillingItemType.USAGE) {
    return billingItem.quantity;
  }
  const quantity =
    billingItem.quantity *
    calculateActiveDays(month, billingItem.startDate, billingItem.endDate, billingItem.paidUntil);
  return Math.round(quantity * 100) / 100;
};

export const getBillingItemProratedPrice = (billingItem: BillingItem, month: string) => {
  if (billingItem.isDemo) {
    return new Money(0);
  }
  return billingItem.unitPrice
    .applyPercentageDiscount(billingItem.discount)
    .multiply(getBillingItemQuantity(billingItem, month));
};

export const filterBillingItemsForMonth = (billingItems: BillingItem[], month: string) => {
  const monthStart = DateTime.fromFormat(month, 'yyyy-MM').startOf('month');
  const monthEnd = DateTime.fromFormat(month, 'yyyy-MM').endOf('month');

  return (
    billingItems?.filter((item: BillingItem) => {
      if (item.type === BillingItemType.ONE_TIME) {
        const datePaid = DateTime.fromISO(item.datePaid);
        if (datePaid.toFormat('yyyy-MM') === month) {
          return true;
        }
        return false;
      }
      if (item.type === BillingItemType.USAGE) {
        if (item.period === month) {
          return true;
        }
        return false;
      }
      const startDate = item.startDate ? DateTime.fromISO(item.startDate) : null;
      const endDate = item.endDate ? DateTime.fromISO(item.endDate) : null;

      if (!startDate) return false;

      if (startDate > monthEnd) return false;

      if (endDate && endDate < monthStart) return false;

      return true;
    }) || []
  );
};
